import React, { useEffect } from "react";

declare global {
  interface Window {
    GbSdk: any;
    GbLoadInit: () => void;
    GbCustomLoader: () => void;
    gbPositioningInterval: any;
  }
}

interface Props {
  user: {
    subject: string;
    user_info: {
      user_id: number;
      full_name: string;
      email: string;
      first_name: string;
      last_name: string;
      gender?: string;
      date_of_birth?: string;
      creation_date?: string;
    };
  };
}

const API_KEY = process.env.REACT_APP_GAMEBALL_API_KEY;

const GameballProvider: React.FC<Props> = ({ user }) => {
  useEffect(() => {
    // Check for required identifier
    if (!user.subject) {
      console.warn('GameballProvider: missing required subject identifier');
      return;
    }

    // ---- 1) Cleanup old scripts & intervals ----
    document
      .querySelectorAll(
        'script[data-gameball-init], script[data-gameball-loader], script[data-gameball-position], script[src*="gameball-init.min.js"]'
      )
      .forEach((s) => s.remove());
    if (window.gbPositioningInterval) {
      clearInterval(window.gbPositioningInterval);
      window.gbPositioningInterval = null;
    }

    // ---- 2) Inject CSS to force **all** Gameball pieces to bottom-right ----
    const styleTag = document.createElement("style");
    styleTag.setAttribute("data-gameball-position-css", "true");
    styleTag.innerHTML = `
      /* BUTTON iframe: always bottom-right */
      #btnIframe,
      .gb_btn_container,
      .gb-button-iframe {
        @media (min-width: 768px) {
          bottom: 5px !important;
          right: 5px !important;
          left: auto !important;
        }
      }

      /* PANEL container: position itself bottom-right */
      .gb_protect {
        @media (min-width: 768px) {
          bottom: 90px !important;
          right: 20px !important;
          left: auto !important;
          transform: none !important;
          max-height: 80vh !important;
        }
      }

      /* The actual <iframe> that slides out: override any left-based slide-in */
      #gbWidgetContainer iframe {
        @media (min-width: 768px) {
          bottom: 90px !important;
          right: 20px !important;
          left: auto !important;
          transform: none !important;
        }
      }
    `;
    document.head.appendChild(styleTag);

    // ---- 3) Custom loader override (JSON → CDN) ----
    const loaderScript = document.createElement("script");
    loaderScript.setAttribute("data-gameball-loader", "true");
    loaderScript.innerHTML = `
      window.GbCustomLoader = function() {
        const originalFetch = window.fetch;
        window.fetch = function(url, options) {
          if (typeof url === 'string') {
            if (url.includes('localhost') && url.includes('/dist/widget/data/')) {
              const f = url.split('/').pop().split('?')[0];
              return originalFetch('https://assets.gameball.co/widget/data/' + f, options);
            } else if (url.endsWith('.json') &&
                       (url.includes('gameball') || url.includes('/widget/')) &&
                       !url.includes('://')) {
              const f = url.split('/').pop();
              return originalFetch('https://assets.gameball.co/widget/config/' + f, options);
            }
          }
          return originalFetch(url, options);
        };
        const origOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(m, u, ...a) {
          if (typeof u === 'string') {
            if (u.includes('localhost') && u.includes('/dist/widget/data/')) {
              const f = u.split('/').pop().split('?')[0];
              return origOpen.call(this, m, 'https://assets.gameball.co/widget/data/' + f, ...a);
            } else if (u.endsWith('.json') &&
                       (u.includes('gameball') || u.includes('/widget/')) &&
                       !u.includes('://')) {
              const f = u.split('/').pop();
              return origOpen.call(this, m, 'https://assets.gameball.co/widget/config/' + f, ...a);
            }
          }
          return origOpen.call(this, m, u, ...a);
        };
        const obs = new MutationObserver((ms) => {
          ms.forEach((m) =>
            m.addedNodes.forEach((n) => {
              if (
                n.nodeName === 'SCRIPT' &&
                n.src &&
                n.src.includes('localhost') &&
                n.src.includes('/dist/widget/')
              ) {
                const path = new URL(n.src).pathname;
                const s = document.createElement('script');
                s.src = 'https://assets.gameball.co' + path;
                s.async = true; s.defer = true;
                n.parentNode?.replaceChild(s, n);
              }
            })
          );
        });
        obs.observe(document, { childList: true, subtree: true });
      };
      window.GbCustomLoader();
    `;
    document.head.appendChild(loaderScript);

    // ---- 4) Init script ----
    const initScript = document.createElement("script");
    initScript.setAttribute("data-gameball-init", "true");
    initScript.innerHTML = `
      window.GbLoadInit = function(){
        GbSdk.init({
          APIKey: '${API_KEY}',
          lang: 'en',
          playerUniqueId: '${user.subject}',
          playerAttributes: {
            displayName: '${user.user_info.full_name || ""}',
            email: '${user.user_info.email || ""}',
            firstName: '${user.user_info.first_name || ""}',
            lastName: '${user.user_info.last_name || ""}',
            gender: '${user.user_info.gender || ""}',
            dateOfBirth: '${user.user_info.date_of_birth || ""}',
            joinDate: '${user.user_info.creation_date?.split(' ')[0] || ""}',
            custom: { portal: 'patient' }
          },
        });
      };
    `;
    document.head.appendChild(initScript);

    // ---- 5) Load the official Gameball script ----
    const scriptSrc = document.createElement("script");
    scriptSrc.defer = true;
    scriptSrc.src = "https://assets.gameball.co/widget/js/gameball-init.min.js";
    document.head.appendChild(scriptSrc);

    // ---- cleanup on unmount ----
    return () => {
      clearInterval(window.gbPositioningInterval);
      [styleTag, loaderScript, initScript, scriptSrc].forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [user]);

  return null;
};

export default GameballProvider;
